import { useEffect } from 'react';

export const SITE_URL = 'https://kiaelectromotor.com';

export const companySchema = {
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: 'شرکت کیا الکتروموتور پارت',
  alternateName: 'کیا الکتروموتور',
  url: SITE_URL,
  logo: `${SITE_URL}/images/kep-logo.webp`,
  image: `${SITE_URL}/images/hero-factory-real-v2.webp`,
  email: 'info@kiaelectromotor.com',
  telephone: ['+982191009883', '+982165028992'],
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'شهرک صنعتی صفادشت، خیابان دهم غربی، پلاک ۱۶۴',
    addressLocality: 'صفادشت',
    addressRegion: 'استان تهران',
    addressCountry: 'IR',
  },
  areaServed: 'ایران',
  knowsAbout: ['طراحی و تولید الکتروموتور', 'الکتروموتورهای صنعتی', 'الکتروموتور هود و کولری', 'فن آکسیال و تانژانت', 'الکتروموتور براشلس BLDC'],
};

type SeoHeadProps = {
  title: string;
  description: string;
  canonicalPath?: string;
  image?: string;
  type?: 'website' | 'article' | 'product';
  noindex?: boolean;
  keywords?: string[];
  model3DUrl?: string | null;
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
};

const upsertMeta = (selector: string, content: string) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    const match = selector.match(/(?:name|property)=["']([^"']+)["']/);
    if (match) element.setAttribute(selector.includes('property=') ? 'property' : 'name', match[1]);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const SeoHead = ({ title, description, canonicalPath = '/', image = '/images/hero-factory-real-v2.webp', type = 'website', noindex = false, keywords = [], model3DUrl = null, structuredData }: SeoHeadProps) => {
  useEffect(() => {
    const canonical = new URL(canonicalPath, SITE_URL).toString();
    const imageUrl = new URL(image, SITE_URL).toString();
    document.title = title;
    document.documentElement.lang = 'fa';
    document.documentElement.dir = 'rtl';
    upsertMeta('meta[name="description"]', description);
    upsertMeta('meta[name="author"]', 'شرکت کیا الکتروموتور پارت');
    const hasQueryString = window.location.search.length > 0;
    upsertMeta('meta[name="robots"]', noindex || hasQueryString ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');
    upsertMeta('meta[name="keywords"]', keywords.join(', '));
    upsertMeta('meta[property="og:title"]', title);
    upsertMeta('meta[property="og:description"]', description);
    upsertMeta('meta[property="og:type"]', type === 'product' ? 'product' : type);
    upsertMeta('meta[property="og:url"]', canonical);
    upsertMeta('meta[property="og:image"]', imageUrl);
    upsertMeta('meta[property="og:site_name"]', 'کیا الکتروموتور پارت');
    upsertMeta('meta[property="og:locale"]', 'fa_IR');
    upsertMeta('meta[name="twitter:card"]', 'summary_large_image');
    upsertMeta('meta[name="twitter:title"]', title);
    upsertMeta('meta[name="twitter:description"]', description);
    upsertMeta('meta[name="twitter:image"]', imageUrl);
    let canonicalLink = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonical;
    document.head.querySelector('link[data-seo="3d-model"]')?.remove();
    if (model3DUrl) {
      const modelLink = document.createElement('link');
      modelLink.rel = 'alternate';
      modelLink.type = 'text/html';
      modelLink.href = model3DUrl;
      modelLink.title = `3D model of ${title}`;
      modelLink.setAttribute('data-seo', '3d-model');
      document.head.appendChild(modelLink);
    }
    document.head.querySelector('#site-structured-data')?.remove();
    const schema = document.createElement('script');
    schema.id = 'site-structured-data';
    schema.type = 'application/ld+json';
    schema.textContent = JSON.stringify(structuredData || { '@context': 'https://schema.org', '@type': 'WebPage', name: title, description, url: canonical, isPartOf: { '@id': `${SITE_URL}/#website` } });
    document.head.appendChild(schema);
  }, [canonicalPath, description, image, keywords, model3DUrl, noindex, structuredData, title, type]);
  return null;
};

export default SeoHead;
